import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomerLayout from '../components/CustomerLayout';
import { customerAPI } from '../lib/api';
import '../App.css';

export default function TicketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);

    useEffect(() => {
        fetchTicketDetails();
    }, [id]);

    const fetchTicketDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await customerAPI.getTicket(id);
            setTicket(response.data);
        } catch (err) {
            console.error('Error fetching ticket details:', err);
            setError(err.response?.data?.error || 'Failed to load ticket details');
        } finally {
            setLoading(false);
        }
    };

    const addComment = async (e) => {
        e.preventDefault();
        
        if (!newComment.trim()) {
            alert('Please enter a comment');
            return;
        }

        setAddingComment(true);
        try {
            await customerAPI.addTicketComment(ticket.ticket_id, newComment);
            setNewComment('');
            // Refresh ticket details
            fetchTicketDetails();
        } catch (err) {
            console.error('Error adding comment:', err);
            alert(err.response?.data?.error || 'Failed to add comment');
        } finally {
            setAddingComment(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open': return '#007bff';
            case 'in progress': return '#ffc107';
            case 'resolved': return '#28a745';
            case 'closed': return '#6c757d';
            default: return '#6c757d';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'low': return '#28a745';
            case 'normal': return '#007bff';
            case 'high': return '#ffc107';
            case 'urgent': return '#dc3545';
            default: return '#007bff';
        }
    };

    if (loading) {
        return (
            <CustomerLayout>
                <div style={{ maxWidth: '1200px' }}>
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div className="spinner"></div>
                        <p style={{ marginTop: '20px', color: '#666' }}>Loading ticket...</p>
                    </div>
                </div>
            </CustomerLayout>
        );
    }

    if (error || !ticket) {
        return (
            <CustomerLayout>
                <div style={{ maxWidth: '1200px' }}>
                    <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
                        <p style={{ fontSize: '18px', color: '#dc3545', marginBottom: '20px' }}>
                            {error || 'Ticket not found'}
                        </p>
                        <button 
                            className="btn btn-primary"
                            onClick={() => navigate('/tickets')}
                        >
                            Back to Tickets
                        </button>
                    </div>
                </div>
            </CustomerLayout>
        );
    }

    return (
        <CustomerLayout>
            <div style={{ maxWidth: '1200px' }}>
                {/* Header with back button */}
                <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button 
                        className="btn btn-secondary"
                        onClick={() => navigate('/tickets')}
                        style={{ padding: '8px 16px' }}
                    >
                        ← Back
                    </button>
                    <div>
                        <h1 style={{ margin: 0 }}>Ticket #{ticket.ticket_number}</h1>
                        <p style={{ color: '#666', margin: '5px 0 0 0' }}>{ticket.subject}</p>
                    </div>
                </div>

                {/* Ticket Details Card */}
                <div className="card" style={{ marginBottom: '20px' }}>
                    {/* Status Bar */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '20px',
                        padding: '20px',
                        background: '#f8f9fa',
                        borderRadius: '4px',
                        marginBottom: '30px'
                    }}>
                        <div>
                            <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Status</h4>
                            <span
                                className="status-badge"
                                style={{ 
                                    backgroundColor: getStatusColor(ticket.status), 
                                    color: 'white',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                }}
                            >
                                {ticket.status}
                            </span>
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Priority</h4>
                            <span
                                className="status-badge"
                                style={{ 
                                    backgroundColor: getPriorityColor(ticket.priority), 
                                    color: 'white',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                }}
                            >
                                {ticket.priority}
                            </span>
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Created</h4>
                            <p style={{ margin: 0 }}>{formatDate(ticket.created_at)}</p>
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Last Updated</h4>
                            <p style={{ margin: 0 }}>{formatDate(ticket.updated_at)}</p>
                        </div>
                        {ticket.assigned_to_name && (
                            <div>
                                <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Assigned To</h4>
                                <p style={{ margin: 0 }}>{ticket.assigned_to_name}</p>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ marginBottom: '15px' }}>Description</h3>
                        <div style={{ 
                            background: '#f8f9fa', 
                            padding: '20px', 
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            border: '1px solid #dee2e6'
                        }}>
                            {ticket.description}
                        </div>
                    </div>

                    {/* Comments/Conversation */}
                    <div>
                        <h3 style={{ marginBottom: '20px' }}>
                            Conversation 
                            {ticket.comments && ticket.comments.filter(c => !c.is_private).length > 0 && (
                                <span style={{ color: '#666', fontSize: '14px', fontWeight: 'normal', marginLeft: '10px' }}>
                                    ({ticket.comments.filter(c => !c.is_private).length} {ticket.comments.filter(c => !c.is_private).length === 1 ? 'reply' : 'replies'})
                                </span>
                            )}
                        </h3>

                        {/* Comments List */}
                        {ticket.comments && ticket.comments.filter(c => !c.is_private).length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                {ticket.comments.filter(c => !c.is_private).map((comment, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            background: comment.user_id === ticket.customer_id || !comment.user_id ? '#e8f4ff' : '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '8px',
                                            marginBottom: '15px',
                                            border: '1px solid ' + (comment.user_id === ticket.customer_id || !comment.user_id ? '#b8daff' : '#dee2e6')
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                            <div>
                                                <strong style={{ fontSize: '16px' }}>
                                                    {(comment.user_id === ticket.customer_id || !comment.user_id) 
                                                        ? (ticket.customer_name || 'Customer')
                                                        : (comment.author_name || 'Support')}
                                                </strong>
                                                {(comment.user_id === ticket.customer_id || !comment.user_id) && (
                                                    <span style={{ 
                                                        marginLeft: '10px',
                                                        fontSize: '12px',
                                                        padding: '2px 8px',
                                                        background: '#007bff',
                                                        color: 'white',
                                                        borderRadius: '4px'
                                                    }}>
                                                        Customer Reply
                                                    </span>
                                                )}
                                                {comment.author_name && comment.user_id !== ticket.customer_id && (
                                                    <span style={{ 
                                                        marginLeft: '10px',
                                                        fontSize: '12px',
                                                        padding: '2px 8px',
                                                        background: '#6c757d',
                                                        color: 'white',
                                                        borderRadius: '4px'
                                                    }}>
                                                        Support
                                                    </span>
                                                )}
                                            </div>
                                            <span style={{ fontSize: '14px', color: '#666' }}>
                                                {formatDate(comment.created_at)}
                                            </span>
                                        </div>
                                        <p style={{ 
                                            whiteSpace: 'pre-wrap', 
                                            margin: 0,
                                            lineHeight: '1.6'
                                        }}>
                                            {comment.comment}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add Reply Form */}
                        {ticket.status?.toLowerCase() !== 'closed' && (
                            <div style={{ 
                                background: 'white',
                                padding: '25px',
                                borderRadius: '8px',
                                border: '2px solid #007bff'
                            }}>
                                <h4 style={{ marginBottom: '15px' }}>Add Reply</h4>
                                <form onSubmit={addComment}>
                                    <textarea
                                        className="form-input"
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        rows="5"
                                        placeholder="Type your reply here..."
                                        disabled={addingComment}
                                        style={{ marginBottom: '15px' }}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setNewComment('')}
                                            disabled={addingComment || !newComment.trim()}
                                        >
                                            Clear
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={addingComment || !newComment.trim()}
                                        >
                                            {addingComment ? 'Sending...' : '📤 Send Reply'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {ticket.status?.toLowerCase() === 'closed' && (
                            <div style={{ 
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                textAlign: 'center'
                            }}>
                                <p style={{ color: '#666', margin: 0 }}>
                                    ℹ️ This ticket is closed. Please create a new ticket if you need further assistance.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </CustomerLayout>
    );
}
